<template>
  <div id="AccountChangePassword" class="Main-Content">
    <ValidationObserver v-slot="{ invalid }" slim>
      <Page
        :title="$t('Password')"
        :primaryAction="{
          label: $t('Update'),
          onAction: save,
          disabled: invalid || complexityError || saving
        }"
        :secondaryActions="[{
          label: $t('Notes'),
          onAction: () => eventHub.$emit('showAccountNotes', 'USER')
        }, {
          label: $t('Log out of all other browsers'),
          onAction: logoutBrowsers,
          disabled: saving
        }]"
      >
        <LoadingLayout v-if="loading"/>

        <Layout v-else-if="theUser.accountId !== theUser.onBehalfOfAccountId">
          <LayoutSection>
            <Card>
              <CardSection :title="$t('No access')">
                <div class="ErrorText">
                  <Icon icon="fas fas fa-exclamation-triangle fa-2x" />
                  <p>{{ $t('AccountPassword.noAccess') }}</p>
                </div>
              </CardSection>
            </Card>
          </LayoutSection>
        </Layout>

        <Layout v-else>
          <LayoutSection>
            <Card>
              <CardSection :title="$t('Change Your Password')">

                <FormLayout>
                  <!-- not sure exactly how to handle validation here -->
                  <TextField
                    :label="$t('Current Password')"
                    v-model="oldPassword"
                    :error="formErrors.oldPassword"
                    type="password"
                  />
                  <TextField
                    :label="$t('New Password')"
                    v-model="newPassword"
                    :error="formErrors.newPassword || complexityError"
                    type="password"
                  />
                  <TextField
                    :label="$t('Confirm')"
                    v-model="confirmNewPassword"
                    :error="formErrors.confirmNewPassword"
                    type="password"
                  />
                </FormLayout>

              </CardSection>
            </Card>
            <Card v-if="complexityExists">
              <CardSection>
                {{$t('AccountSecurity.passwordFollowRequirements')}}
                <div class="complexity-list">
                  <List tight>
                    <ListItem v-if="complexitySettings.minimumLength">{{$t('AccountSecurity.minLengthVal', { value: complexitySettings.minimumLength })}}</ListItem>
                    <ListItem v-if="complexitySettings.requiresSymbol">{{$t('AccountSecurity.atLeastOneSymbolVal', { value: symbolsAllowed })}}</ListItem>
                    <ListItem v-if="complexitySettings.requiresNumber">{{$t('AccountSecurity.atLeastOneNumber')}}</ListItem>
                    <ListItem v-if="complexitySettings.requiresMixedCase">{{$t('AccountSecurity.atLeastOneMixedCase')}}</ListItem>
                  </List>
                </div>
              </CardSection>
            </Card>
          </LayoutSection>
        </Layout>

      </Page>
    </ValidationObserver>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'AccountChangePassword',
  data () {
    return {
      loading: true,
      saving: false,
      oldPassword: null,
      newPassword: null,
      confirmNewPassword: null,
      complexitySettings: {},
      complexityExists: false,
      formErrors: {},
      symbolsAllowed: `~\`!@#$%^&*()-[]{}|_+=\\"':;<,>.?/`,
      symbolsRegex: /[~`!@#$%^&*()\-[\]{}|_+=\\"':;<,>.?/]/g,
      numberRegex: /[0-9]/g,
      upperCaseRegex: /[A-Z]/g,
      lowerCaseRegex: /[a-z]/g
    }
  },
  mounted() {
    if (this.theUser.accountId !== this.theUser.onBehalfOfAccountId) {
      this.$router.replace('/account/company/')
      return
    }
    this.api.getCurrentAccountSecurity()
      .then((data) => {
        this.complexitySettings = data && data.securityPasswordComplexity
        this.complexityExists = !_.isEmpty(this.complexitySettings)
        this.loading = false
      })
      .catch(this.apiCatch)

  },
  computed: {
    complexityError() {
      if (!this.complexityExists || !this.newPassword) return false
      if (this.complexitySettings.minimumLength > this.newPassword.length) {
        return this.$t('AccountSecurity.passwordDoesNotMeetMin')
      }
      if (this.complexitySettings.requiresSymbol) {
        const matches = this.newPassword.match(this.symbolsRegex)
        if (!matches) return this.$t('AccountSecurity.passwordDoesNotContainSymbol')
      }
      if (this.complexitySettings.requiresNumber) {
        const matches = this.newPassword.match(this.numberRegex)
        if (!matches) return this.$t('AccountSecurity.passwordDoesNotContainNumber')
      }
      if (this.complexitySettings.requiresMixedCase) {
        const upperCase = this.newPassword.match(this.upperCaseRegex)
        const lowerCase = this.newPassword.match(this.lowerCaseRegex)
        if (!upperCase || !lowerCase) return this.$t('AccountSecurity.passwordDoesNotContainMixedCase')
      }
      return false
    }
  },
  methods: {
    logoutBrowsers() {
      this.saving = true
      NProgress.start()
      this.api.logoutOtherSessions()
        .then(() => {
          this.$ui.toast({
            title: this.$t('Complete')
          })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    },
    save() {
      if (!this.oldPassword || !this.newPassword || this.newPassword !== this.confirmNewPassword) {
        this.formErrors.oldPassword = 'Required'
        this.formErrors.newPassword = 'Required'
        this.formErrors.confirmNewPassword = 'Required'
        this.$forceUpdate()
        return
      }

      NProgress.start()
      this.saving = true
      this.api.changePassword(this.oldPassword, this.newPassword)
        .then(() => {
          this.$ui.toast({
            title: this.$t('Password Changed')
          })
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
          this.saving = false
          // dont really know what to do after response
          this.currentPassword = null
          this.newPassword = null
          this.confirmNewPassword = null
        })
    }
  },
  components: {
  }
}

</script>

<style lang="scss">
#AccountChangePassword {
  .complexity-list {
    padding-top: 8px;
  }
}
</style>